/**
 * **One date unlike its weekday** — an override (booking-system R3): other windows on that date,
 * or none, which closes it. The „Nowy wyjątek na dzień" dialog (T-B2w) authors one.
 *
 * It is not a weekday of the pattern, and the server does not treat it as one
 * (`AvailabilityProcessor.SetDateOverrideAsync`): at most TEN windows rather than the week's
 * seventy, an EMPTY list is the date closed — a statement, not an omission — and the date has to lie
 * inside the calendar's window (`CalendarDateBounds`: no earlier than a day ago, no later than 730
 * days ahead). This screen is STRICTER than the server on the far end: a date is stated no later
 * than the last day the Grafik tab reads ({@link lastStatableDay}), because an exception saved past
 * that would vanish from „Inaczej niż zwykle" the moment it was saved. What it shares with the week is the shape rule, the same `WindowGridValidator`, so
 * the shape is judged by {@link dayVerdict}. `PUT …/override/{date}` OVERWRITES: authoring on a
 * date that already has an exception changes it, and the draft opens on what that date holds.
 *
 * The date is a civil date on the TRAINER's clock, never the reader's.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type {
  TrainWithMeModulesBookingDtosRequestsPutDateOverrideRequest as PutDateOverrideRequest,
  TrainWithMeModulesBookingDtosResponsesTrainerAvailabilityResponse as TrainerAvailabilityResponse,
} from '~/shared/api/generated/model';

import { paddedClock, shiftDay, type CivilDay } from './trainerClock';
import {
  dayVerdict,
  LAST_MINUTE,
  nextWindowSeed,
  type PatternWindow,
  type WindowProblem,
} from './weekPattern';
import { workingDays, type WorkingWindow } from './workingHours';

/** `AvailabilityProcessor.MaxOverrideWindows`. */
export const MAX_DATE_WINDOWS = 10;

/**
 * How far past today the Grafik tab reads its exceptions and blocks: one read covers at most 180
 * days (`BookingDefaults.MaxRangeDays`, R3), and it starts yesterday.
 */
export const SCHEDULE_READ_AHEAD_DAYS = 178;

/**
 * The last date an exception may be stated on, and the last day a block may reach into: the last
 * day the tab reads, so whatever is saved stays on the list that shows it.
 */
export function lastStatableDay(today: CivilDay): CivilDay {
  return shiftDay(today, SCHEDULE_READ_AHEAD_DAYS);
}

export interface ExceptionDraft {
  readonly date: CivilDay;
  readonly closed: boolean;
  readonly windows: readonly PatternWindow[];
  readonly seq: number;
  /** The date already carries an exception, and saving changes it. */
  readonly replacing: boolean;
  /** The trainer changed the hours or the switch: moving the date keeps them. */
  readonly touched: boolean;
}

export type DateProblem = 'past' | 'tooFar';

export interface ExceptionVerdict {
  readonly bad: readonly string[];
  readonly problem: WindowProblem | null;
  readonly dateProblem: DateProblem | null;
  /** Whether „Dodaj okno" has room: under ten windows, and a minute left before 23:59. */
  readonly canAdd: boolean;
  readonly atLimit: boolean;
  /** Open, with no windows and not closed: nothing to state yet. */
  readonly empty: boolean;
  readonly canSave: boolean;
}

/** What the date says today — its override, or its weekday's windows — and where it came from. */
export function dateHours(
  availability: TrainerAvailabilityResponse,
  date: CivilDay,
): { readonly windows: readonly WorkingWindow[]; readonly overridden: boolean } {
  const [working] = workingDays(availability, [date]);
  return {
    windows: working?.windows ?? [],
    overridden: working?.source === 'override',
  };
}

/** The weekday's own windows on that date, whatever an exception says — „Zwykle: …". */
export function usualHours(
  availability: TrainerAvailabilityResponse,
  date: CivilDay,
): readonly WorkingWindow[] {
  const [working] = workingDays({ ...availability, overrides: [] }, [date]);
  return working?.windows ?? [];
}

/**
 * The dialog's opening state for a date: its standing exception when it has one, otherwise the
 * weekday's usual hours to change from — an exception is nearly always „like usual, but…" — and on
 * a usually free date, the working morning.
 */
export function exceptionDraftFor(
  availability: TrainerAvailabilityResponse,
  date: CivilDay,
): ExceptionDraft {
  const { windows, overridden } = dateHours(availability, date);
  const fromRead = windows.map((window) => ({
    start: window.start,
    // The read widens an end at midnight to 1440; this wire stores the day's last minute.
    end: Math.min(window.end, LAST_MINUTE),
  }));
  // A usually free date opens on the working morning: an exception there adds hours.
  const seed = nextWindowSeed([]);
  const hours = !overridden && fromRead.length === 0 && seed !== null ? [seed] : fromRead;
  const seeded = hours.map((window, index) => ({ id: `x${String(index + 1)}`, ...window }));
  return {
    date,
    closed: overridden && windows.length === 0,
    windows: seeded,
    seq: seeded.length,
    replacing: overridden,
    touched: false,
  };
}

/**
 * The dialog moved to another date. Hours the trainer has not touched follow the date — they were
 * only the old date's reading — but hours they edited stay, and only the date and whether it
 * already carries an exception change.
 */
export function exceptionMovedTo(
  draft: ExceptionDraft,
  availability: TrainerAvailabilityResponse,
  date: CivilDay,
): ExceptionDraft {
  if (!draft.touched) return exceptionDraftFor(availability, date);
  return { ...draft, date, replacing: dateHours(availability, date).overridden };
}

export function exceptionWindowAdded(draft: ExceptionDraft): ExceptionDraft {
  const seed = nextWindowSeed(draft.windows);
  if (seed === null || draft.windows.length >= MAX_DATE_WINDOWS) return draft;
  const seq = draft.seq + 1;
  return {
    ...draft,
    seq,
    touched: true,
    windows: [...draft.windows, { id: `x${String(seq)}`, ...seed }],
  };
}

export function exceptionWindowRetimed(
  draft: ExceptionDraft,
  id: string,
  change: { readonly start?: number; readonly end?: number },
): ExceptionDraft {
  return {
    ...draft,
    touched: true,
    windows: draft.windows.map((window) => (window.id === id ? { ...window, ...change } : window)),
  };
}

export function exceptionWindowRemoved(draft: ExceptionDraft, id: string): ExceptionDraft {
  return { ...draft, touched: true, windows: draft.windows.filter((window) => window.id !== id) };
}

export function exceptionClosedToggled(draft: ExceptionDraft): ExceptionDraft {
  return { ...draft, touched: true, closed: !draft.closed };
}

/** The whole dialog judged at once, in the order a trainer meets it: the date, then the hours. */
export function exceptionVerdict(draft: ExceptionDraft, today: CivilDay): ExceptionVerdict {
  const dateProblem: DateProblem | null =
    draft.date < today ? 'past' : draft.date > lastStatableDay(today) ? 'tooFar' : null;
  // A closed day IS the statement: the windows under the switch are not sent, so not judged.
  const shape = draft.closed ? { bad: [], problem: null } : dayVerdict(draft.windows);
  const atLimit = draft.windows.length >= MAX_DATE_WINDOWS;
  const empty = !draft.closed && draft.windows.length === 0;
  return {
    bad: shape.bad,
    problem: shape.problem,
    dateProblem,
    canAdd: !draft.closed && !atLimit && nextWindowSeed(draft.windows) !== null,
    atLimit,
    empty,
    canSave: dateProblem === null && shape.problem === null && !empty,
  };
}

/** `PUT /trainer-availability/override/{date}`'s body. A closed date sends an EMPTY list. */
export function exceptionRequest(draft: ExceptionDraft): PutDateOverrideRequest {
  if (draft.closed) return { windows: [] };
  return {
    windows: [...draft.windows]
      .sort((a, b) => a.start - b.start)
      .map((window) => ({ start: paddedClock(window.start), end: paddedClock(window.end) })),
  };
}
